import type { ActiveVote } from "@/appInterface/windows/ActiveVote";
import type { WidgetData } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import type { elementBase } from "@/elementBase/elementBase";
import type { Group } from "@/group";
import type { ActiveVoteTool } from "@/tools/ActiveVoteTool";
import { getScaledSize } from "@/utils";

const B_WIDTH = 70;
const B_ONLY_MINUS_WIDTH = 48;
const B_HEIGHT = 24;

const MARGIN_NUMBER = 11;
const HEIGHT_AND_WIDTH = 10.5;
const CONTROL_BORDER_RADIUS = 10.5;

const SELECTOR_RADIUS = 12;
const LINE_WIDTH = 1.5;

const TOOLTIP_WIDTH = 230;
const TOOLTIP_HEIGHT = 32;
const TOOLTIP_RADIUS = 9;

export type ControlType = {
    group: Group;
    widget: WidgetData;
    groupUID: string;
    widgetUID: string;
};

type HoveredSide = "minus" | "plus" | null;

export class ActiveVoteInterface {
    constructor(data: ActiveVote) {
        this.activeVote = data;
        this.init();
    }

    init() {
        canv.toolBox.hide();
        canv.toolBox.switchToByName("activeVoteTool");
        this.setCandidates();
        this.zoomingToCandidates();
    }

    remove() {
        canv.toolBox.show();
        canv.toolBox.switchToByName("cursorTool");
        this.clearCandidates();
    }

    setCandidates() {
        if (!this.activeVote.data) return;

        const widgetUIDs = new Set(
            Object.values(this.activeVote.data.children).map((child) => child.linkToOriginalUID),
        );

        const candidatesGroup = canv.canvasController.items.filter((item) => {
            return item.children.some(<T extends elementBase>(child: Group | T) =>
                widgetUIDs.has(child.UID),
            );
        });

        if (candidatesGroup.length > 0) {
            const map = new Map();
            const controlsData: ControlType[] = candidatesGroup.flatMap((group) => {
                if (group.isItemWrapper()) {
                    const widget = group.children[0] as unknown as WidgetData;
                    map.set(widget.UID, widget);
                    return [
                        {
                            group,
                            widget,
                            groupUID: group.UID,
                            widgetUID: widget.UID,
                        },
                    ];
                }

                const selectedGroups = group.children.filter((innerGroup): innerGroup is Group => {
                    if ("children" in innerGroup) {
                        return widgetUIDs.has(innerGroup.children[0].UID);
                    }

                    return false;
                });

                return selectedGroups.map((group) => {
                    const widget = group.children[0] as unknown as WidgetData;

                    return {
                        group,
                        widget,
                        groupUID: group.UID,
                        widgetUID: widget.UID,
                    };
                });
            });

            this.candidates = map;
            this.controlsData = controlsData;
            this.candidateUIDs = widgetUIDs;
        } else {
            console.error("Error: Active vote, no candidates for zoom");
            this.clearCandidates();
        }
    }

    clearCandidates() {
        this.candidates = new Map();
        this.candidateUIDs = new Set();
    }

    zoomingToCandidates() {
        if (this.candidates.size > 0) {
            canv.zoomToVoteCandidates(Array.from(this.candidates.values()));
        }
    }

    getWidgetVotes = (widgetUID: string) => {
        if (!this.activeVote.data) return 0;
        return this.activeVote.data.children[widgetUID]?.votes?.filter(
            (vote) => vote.token === canv.getInitDataProp("userToken"),
        )?.length;
    };

    drawCircle(
        x: number,
        y: number,
        r: number,
        fillColor: string,
        strokeColor?: string,
        lineWidth?: number,
    ) {
        canv.ctx.beginPath();
        canv.ctx.arc(x, y, r, 0, 2 * Math.PI);
        canv.ctx.fillStyle = fillColor;
        canv.ctx.fill();
        if (strokeColor) {
            canv.ctx.strokeStyle = strokeColor;
            canv.ctx.lineWidth = lineWidth ?? 1;
            canv.ctx.stroke();
        }
    }

    drawText(text: string, x: number, y: number, fontSize: number, color: string) {
        canv.ctx.fillStyle = color;
        canv.ctx.textAlign = "center";
        canv.ctx.textBaseline = "middle";
        canv.ctx.font = `normal ${fontSize}px sans-serif`;
        canv.ctx.fillText(text, x, y);
    }

    drawMinus(x: number, y: number, size: number, color: string, lineWidth: number) {
        canv.ctx.strokeStyle = color;
        canv.ctx.lineWidth = lineWidth;
        canv.ctx.lineCap = "round";
        canv.ctx.beginPath();
        canv.ctx.moveTo(x - size / 2, y);
        canv.ctx.lineTo(x + size / 2, y);
        canv.ctx.stroke();
    }

    drawPlus(x: number, y: number, size: number, color: string, lineWidth: number) {
        canv.ctx.strokeStyle = color;
        canv.ctx.lineWidth = lineWidth;
        canv.ctx.lineCap = "round";
        canv.ctx.beginPath();
        canv.ctx.moveTo(x - size / 2, y);
        canv.ctx.lineTo(x + size / 2, y);
        canv.ctx.moveTo(x, y - size / 2);
        canv.ctx.lineTo(x, y + size / 2);
        canv.ctx.stroke();
    }

    drawActiveControl({
        startX,
        startY,
        width,
        height,
        widgetUID,
    }: {
        startX: number;
        startY: number;
        width: number;
        height: number;
        widgetUID: string;
    }) {
        const isOneVotePerCandidate = this.activeVote.data?.oneVotePerItem === true;
        const isNoAvailableVotes = this.activeVote.availableVotesCount === 0;
        const votesCount = this.getWidgetVotes(widgetUID);
        const centerX = startX + width;
        const centerY = startY + height;

        if (this.isHover && this.hoveredWidget === widgetUID) {
            if (votesCount === 0) {
                this.drawOnlyPlusControl(centerX, centerY);
            } else if (
                this.activeVote.availableVotesCount === 0 ||
                (isOneVotePerCandidate && votesCount > 0)
            ) {
                this.drawOnlyMinusControl(centerX, centerY, votesCount);
            } else {
                this.drawPlusMinusControl(centerX, centerY, votesCount);
            }
        } else {
            if (votesCount === 0) {
                this.drawOnlyPlusControl(centerX, centerY);
            } else {
                this.drawVotesCountControl(centerX, centerY, votesCount);
            }
        }
        this.needDrawTooltip = isNoAvailableVotes;
    }

    drawBorder({
        startX,
        startY,
        width,
        height,
    }: {
        startX: number;
        startY: number;
        width: number;
        height: number;
    }) {
        const viewPort = getViewport();
        const ratio = canv.canvas.width / viewPort.viewportWidth;
        const padding = 5 / ratio;
        const lineWidth = getScaledSize(LINE_WIDTH);

        canv.ctx.save();

        canv.ctx.strokeStyle = "#CDCCD7";
        canv.ctx.lineWidth = lineWidth;
        canv.ctx.beginPath();
        canv.ctx.roundRect(
            startX - padding,
            startY - padding,
            width + 2 * padding,
            height + 2 * padding,
            [8 / ratio, 8 / ratio, 8 / ratio, 8 / ratio],
        );

        canv.ctx.stroke();
        canv.ctx.restore();
    }

    drawOnlyPlusControl(x: number, y: number) {
        canv.ctx.save();
        const isNoAvailableVotes = this.activeVote.availableVotesCount === 0;
        const rCanvas = getScaledSize(SELECTOR_RADIUS);
        const lineWidth = getScaledSize(LINE_WIDTH);

        // фон
        this.drawCircle(
            x,
            y,
            rCanvas,
            "#F5F5F7",
            isNoAvailableVotes ? "#E8E8EE" : "#CDCCD7",
            getScaledSize(1),
        );

        // плюс
        this.drawPlus(x, y, rCanvas, isNoAvailableVotes ? "#ABABBB" : "#2C2D32", lineWidth);

        canv.ctx.restore();
    }

    drawVotesCountControl(x: number, y: number, votesCount: number) {
        canv.ctx.save();
        const rCanvas = getScaledSize(SELECTOR_RADIUS);
        const lineWidth = getScaledSize(1);
        // фон
        this.drawCircle(x, y, rCanvas, "#0d99ff", "", lineWidth);
        // число
        const baseFontSize = (rCanvas * canv.dpr) / 1.5;
        const fontSize = votesCount > 99 ? baseFontSize * 0.7 : baseFontSize;

        this.drawText(votesCount.toString(), x, y, fontSize, "#ffffff");

        canv.ctx.restore();
    }

    drawPlusMinusControl(groupX: number, groupY: number, votesCount: number) {
        canv.ctx.save();
        const bWidth = getScaledSize(B_WIDTH);
        const bHeight = getScaledSize(B_HEIGHT);
        const sRadius = getScaledSize(SELECTOR_RADIUS);
        const cBRadius = getScaledSize(CONTROL_BORDER_RADIUS);
        const margin = getScaledSize(MARGIN_NUMBER);
        const heightAndWidth = getScaledSize(HEIGHT_AND_WIDTH);
        const lineWidth = getScaledSize(LINE_WIDTH);

        const controlX = groupX - bWidth + sRadius;
        const controlY = groupY - bHeight / 2;

        // фон
        canv.ctx.beginPath();
        canv.ctx.roundRect(controlX, controlY, bWidth, bHeight, cBRadius);
        canv.ctx.fillStyle = "#0d99ff";
        canv.ctx.fill();

        //число
        const fontSize = (sRadius * canv.dpr) / 1.5;
        this.drawText(
            votesCount.toString(),
            groupX - bWidth / 2 + sRadius,
            groupY,
            fontSize,
            "#FAFAFA",
        );

        // минус
        const MINUS_X = controlX + margin + heightAndWidth / 2;
        this.drawMinus(MINUS_X, groupY, heightAndWidth, "#FAFAFA", lineWidth);

        // плюс
        const PLUS_X = controlX + bWidth - margin - heightAndWidth / 2;
        this.drawPlus(PLUS_X, groupY, heightAndWidth, "#FAFAFA", lineWidth);

        canv.ctx.restore();
    }

    drawOnlyMinusControl(x: number, y: number, votesCount: number) {
        canv.ctx.save();
        const bWidth = getScaledSize(B_ONLY_MINUS_WIDTH);
        const bHeight = getScaledSize(B_HEIGHT);
        const sRadius = getScaledSize(SELECTOR_RADIUS);
        const cBRadius = getScaledSize(CONTROL_BORDER_RADIUS);
        const margin = getScaledSize(MARGIN_NUMBER);
        const heightAndWidth = getScaledSize(HEIGHT_AND_WIDTH);
        const lineWidth = getScaledSize(LINE_WIDTH);

        // фон
        canv.ctx.fillStyle = "#0d99ff";
        canv.ctx.beginPath();
        canv.ctx.roundRect(x - bWidth + sRadius, y - bHeight / 2, bWidth, bHeight, cBRadius);
        canv.ctx.fill();

        // число
        const fontSize = (sRadius * canv.dpr) / 1.5;
        this.drawText(votesCount.toString(), x, y, fontSize, "#ffffff");

        // минус
        const MINUS_X = x - fontSize / 2 - margin;
        this.drawMinus(MINUS_X, y, heightAndWidth, "#FFFFFF", lineWidth);

        canv.ctx.restore();
    }

    drawToolTip(groupX: number, groupY: number) {
        canv.ctx.save();
        // фон
        const tooltipWidth = getScaledSize(TOOLTIP_WIDTH);
        const tooltipHeight = getScaledSize(TOOLTIP_HEIGHT);
        const tooltipRadius = getScaledSize(TOOLTIP_RADIUS);
        const cBorderRadius = getScaledSize(8);

        const tooltipX = groupX - tooltipWidth / 2;
        const tooltipY = groupY + tooltipHeight - tooltipRadius;

        canv.ctx.beginPath();
        canv.ctx.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, cBorderRadius);
        canv.ctx.fillStyle = "#2C2D32";
        canv.ctx.fill();

        // текс
        const fontSize = (tooltipRadius * canv.dpr) / 1.5;
        this.drawText(
            "Вы использовали все доступные голоса",
            groupX,
            groupY + tooltipHeight - tooltipRadius / 2 + (tooltipHeight - fontSize / 1.5) / 2,
            fontSize,
            "#FAFAFA",
        );

        canv.ctx.restore();
    }

    isOverCircleControl(groupX: number, groupY: number, { x, y }: { x: number; y: number }) {
        const rCanvas = getScaledSize(SELECTOR_RADIUS);
        const dx = x - groupX;
        const dy = y - groupY;
        const distanceSquared = dx * dx + dy * dy;

        return distanceSquared <= rCanvas * rCanvas;
    }

    isOverPlusMinusControl(
        groupX: number,
        groupY: number,
        { x, y }: { x: number; y: number },
    ): HoveredSide {
        const bWidth = getScaledSize(B_WIDTH);
        const bHeight = getScaledSize(B_HEIGHT);
        const sRadius = getScaledSize(SELECTOR_RADIUS);
        const cBorderRadius = getScaledSize(CONTROL_BORDER_RADIUS);

        const controlX = groupX - bWidth + sRadius;
        const controlY = groupY - bHeight / 2;

        const insideMainRect =
            x >= controlX + cBorderRadius &&
            x <= controlX + bWidth - cBorderRadius &&
            y >= controlY &&
            y <= controlY + bHeight;

        const insideVerticalSides =
            x >= controlX &&
            x <= controlX + bWidth &&
            y >= controlY + cBorderRadius &&
            y <= controlY + bHeight - cBorderRadius;

        if (insideMainRect || insideVerticalSides) {
            const centerX = controlX + bWidth / 2;
            return x < centerX ? "minus" : "plus";
        }

        const corners = [
            {
                cx: controlX + cBorderRadius,
                cy: controlY + cBorderRadius,
                side: "minus",
            },
            {
                cx: controlX + bWidth - cBorderRadius,
                cy: controlY + cBorderRadius,
                side: "plus",
            },
            {
                cx: controlX + cBorderRadius,
                cy: controlY + bHeight - cBorderRadius,
                side: "minus",
            },
            {
                cx: controlX + bWidth - cBorderRadius,
                cy: controlY + bHeight - cBorderRadius,
                side: "plus",
            },
        ] as const;

        for (const { cx, cy, side } of corners) {
            const dx = x - cx;
            const dy = y - cy;
            if (dx * dx + dy * dy <= cBorderRadius * cBorderRadius) {
                return side;
            }
        }

        return null;
    }

    isOverOnlyMinusControl(
        groupX: number,
        groupY: number,
        { x, y }: { x: number; y: number },
    ): HoveredSide {
        const bWidth = getScaledSize(B_ONLY_MINUS_WIDTH);
        const bHeight = getScaledSize(B_HEIGHT);
        const sRadius = getScaledSize(SELECTOR_RADIUS);
        const cBorderRadius = getScaledSize(CONTROL_BORDER_RADIUS);

        const btnX = groupX - bWidth + sRadius;
        const btnY = groupY - bHeight / 2;

        const centerX = btnX + bWidth / 2;

        const insideMainRect =
            x >= btnX + cBorderRadius &&
            x <= btnX + bWidth - cBorderRadius &&
            y >= btnY &&
            y <= btnY + bHeight;

        const insideVerticalSides =
            x >= btnX &&
            x <= btnX + bWidth &&
            y >= btnY + cBorderRadius &&
            y <= btnY + bHeight - cBorderRadius;

        if (insideMainRect || insideVerticalSides) {
            return x < centerX ? "minus" : "plus";
        }

        const corners = [
            {
                cx: btnX + cBorderRadius,
                cy: btnY + cBorderRadius,
                side: "minus",
            },
            {
                cx: btnX + bWidth - cBorderRadius,
                cy: btnY + cBorderRadius,
                side: "plus",
            },
            {
                cx: btnX + cBorderRadius,
                cy: btnY + bHeight - cBorderRadius,
                side: "minus",
            },
            {
                cx: btnX + bWidth - cBorderRadius,
                cy: btnY + bHeight - cBorderRadius,
                side: "plus",
            },
        ] as const;

        for (const { cx, cy, side } of corners) {
            const dx = x - cx;
            const dy = y - cy;
            if (dx * dx + dy * dy <= cBorderRadius * cBorderRadius) {
                return side;
            }
        }

        return null;
    }

    getControlByHover({ x, y }: { x: number; y: number }): ControlType | null {
        for (const control of this.controlsData) {
            const { group, widgetUID } = control;
            const groupX = group.startX + group.width;
            const groupY = group.startY + group.height;
            const hasVotes = this.getWidgetVotes(widgetUID) > 0;
            const isOneVotePerCandidate = this.activeVote.data?.oneVotePerItem;

            if (this.isHover && hasVotes) {
                const point = { x, y };

                const overMinusOnlyControl =
                    (this.activeVote.availableVotesCount === 0 &&
                        this.isOverOnlyMinusControl(groupX, groupY, point)) ||
                    (isOneVotePerCandidate &&
                        hasVotes &&
                        this.isOverOnlyMinusControl(groupX, groupY, point));

                const overPlusMinusControl =
                    this.activeVote.availableVotesCount > 0 &&
                    this.isOverPlusMinusControl(groupX, groupY, point);

                if (overMinusOnlyControl || overPlusMinusControl) {
                    return control;
                }
            } else if (this.isOverCircleControl(groupX, groupY, { x, y })) {
                return control;
            }
        }

        return null;
    }

    clickOnlyMinusControl(control: ControlType, x: number, y: number) {
        this.addOrRemoveVote(control, x, y, "only-minus");
    }

    clickPlusMinusControl(control: ControlType, x: number, y: number) {
        this.addOrRemoveVote(control, x, y, "plus-minus");
    }

    clickOnlyPlusControl(control: ControlType, x: number, y: number) {
        this.addOrRemoveVote(control, x, y, "only-plus");
    }

    getGroupControlCoordinates(control: ControlType) {
        return {
            x: control.group.startX + control.group.width,
            y: control.group.startY + control.group.height,
        };
    }

    addOrRemoveVote(
        control: ControlType,
        x: number,
        y: number,
        mode: "plus-minus" | "only-minus" | "only-plus",
    ) {
        const { widgetUID } = control;
        const { x: groupX, y: groupY } = this.getGroupControlCoordinates(control);

        const sidePlusMinus = this.isOverPlusMinusControl(groupX, groupY, { x, y });
        const sideOnlyMinus = this.isOverOnlyMinusControl(groupX, groupY, { x, y });

        switch (mode) {
            case "only-minus":
                if (sideOnlyMinus === "minus") {
                    this.activeVote.removeVoteFromWidget(widgetUID);
                }
                break;
            case "plus-minus":
                if (sidePlusMinus === "plus") {
                    this.activeVote.addVoteToWidget(widgetUID);
                } else if (sidePlusMinus === "minus") {
                    this.activeVote.removeVoteFromWidget(widgetUID);
                }
                break;
            case "only-plus":
                if (this.isOverCircleControl(groupX, groupY, { x, y })) {
                    this.activeVote.addVoteToWidget(widgetUID);
                }
                break;
        }
    }

    handleMouseMove({ x, y }: { x: number; y: number }) {
        const control = this.getControlByHover({ x, y });
        const isCurrentlyHovering = control !== null;

        const needRedraw =
            this.isHover !== isCurrentlyHovering || this.hoveredWidget !== control?.widgetUID;

        this.isHover = isCurrentlyHovering;
        this.hoveredWidget = control?.widgetUID ?? "";

        if (needRedraw) {
            canv.redraw();
        }
    }

    handleMouseUp({ x, y }: { x: number; y: number }) {
        if (!this.isHover) return;

        const control = this.getControlByHover({ x, y });
        const isOneVotePerCandidate = this.activeVote.data?.oneVotePerItem === true;
        if (control) {
            const hasVotes = this.getWidgetVotes(control.widgetUID) > 0;

            if (
                (hasVotes && this.activeVote.availableVotesCount === 0) ||
                (isOneVotePerCandidate && hasVotes)
            ) {
                this.clickOnlyMinusControl(control, x, y);
            } else if (!hasVotes) {
                this.clickOnlyPlusControl(control, x, y);
            } else {
                this.clickPlusMinusControl(control, x, y);
            }
        }
        this.checkIsHover({ x, y });
    }

    checkIsHover({ x, y }: { x: number; y: number }) {
        this.handleMouseMove({ x, y });
        (canv.toolBox.TryGetToolByName("activeVoteTool") as ActiveVoteTool | null)?.updateCursor();
    }

    isHover = false;
    needDrawTooltip = false;
    hoveredWidget = "";

    activeVote: ActiveVote;
    candidates: Map<string, Group> = new Map();
    candidateUIDs: Set<string> = new Set();
    controlsData: ControlType[] = [];
}
